import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ── Status badge helper ── */
const statusBadge = (status) => {
  const map = {
    "Pending":          "ims-badge-pending",
    "Approved":         "ims-badge-approved",
    "Rejected":         "ims-badge-rejected",
    "Issued":           "ims-badge-issued",
    "Partially Issued": "ims-badge-partial",
    "Principal":        "ims-badge-principal",
  };
  return (
    <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>
      {status}
    </span>
  );
};

const RequisitionList = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [users, setUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const fetchRequisitions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || !user) return;
      const params = new URLSearchParams({
        page: String(currentPage + 1),
        limit: String(itemsPerPage),
        search: searchTerm,
      });
      const response = await axios.get(`/requisitions?${params.toString()}`);
      if (Array.isArray(response.data.requisitions)) {
        setRequisitions(response.data.requisitions);
        setTotalPages(response.data.totalPages || 0);
      } else {
        setRequisitions([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      setRequisitions([]);
      setTotalPages(0);
    }
  };

  useEffect(() => {
    fetchRequisitions();
    fetchUsers();
  }, [currentPage, searchTerm, itemsPerPage]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/users");
      const usersMap = {};
      response.data.forEach((u) => { usersMap[u.id] = u.name; });
      setUsers(usersMap);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleOpenRequisition = (id) => {
    if (user.role === "Principal") {
      navigate(`/dashboard/process-requisition/${id}`);
    } else if (user.role === "store_manager") {
      navigate(`/dashboard/issue-requisition/${id}`);
    } else {
      navigate(`/dashboard/view-requisition/${id}`);
    }
  };

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(0);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(requisitions);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Requisitions");
    XLSX.writeFile(workbook, "Requisitions.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Requisition List", 14, 15);
    const tableData = requisitions.map((req, index) => [
      index + 1,
      users[req.user_id] || "Unknown",
      format(new Date(req.expected_date), "dd-MM-yyyy"),
      req.priority.charAt(0).toUpperCase() + req.priority.slice(1),
      req.status,
    ]);
    autoTable(doc, {
      startY: 20,
      head: [["ID", "User", "Expected Date", "Priority", "Status"]],
      body: tableData,
      theme: "grid",
    });
    doc.save("Requisitions.pdf");
  };

  const generateRequisitionPDF = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const pdfWindow = window.open();
      const response = await axios.get(`/requisitions/generate-pdf/${id}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const file = new Blob([response.data], { type: "application/pdf" });
      pdfWindow.location.href = URL.createObjectURL(file);
    } catch (error) {
      console.error("Error opening PDF:", error);
    }
  };

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Requisition List</h4>
          <p>All requisitions across your entity</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-success" onClick={exportToExcel}>
            <i className="fas fa-file-excel mr-1"></i> Excel
          </button>
          <button className="btn btn-sm btn-danger" onClick={exportToPDF}>
            <i className="fas fa-file-pdf mr-1"></i> PDF
          </button>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="row mb-3 align-items-center">
        <div className="col-md-4 d-flex align-items-center">
          <label className="mr-2 mb-0" style={{ whiteSpace: "nowrap", fontSize: "13px" }}>Show:</label>
          <select className="form-control form-control-sm w-auto" value={itemsPerPage} onChange={handleItemsPerPageChange}>
            {[10, 20, 50, 100, 200].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search requisitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-responsive">
        <table className="ims-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>Req ID</th>
              <th>Date Added</th>
              <th>Raised By</th>
              <th>Expected Date</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {requisitions.length > 0 ? (
              requisitions.map((req, index) => (
                <tr key={req.id}>
                  <td>{index + 1 + currentPage * itemsPerPage}</td>
                  <td><strong>#{req.id}</strong></td>
                  <td>{req.created_date}</td>
                  <td>{users[req.user_id] || "Unknown"}</td>
                  <td>{format(new Date(req.expected_date), "dd-MM-yyyy")}</td>
                  <td>
                    <span style={{ fontWeight: 600, fontSize: "12.5px", textTransform: "capitalize" }}>
                      {req.priority}
                    </span>
                  </td>
                  <td>{statusBadge(req.status)}</td>
                  <td>
                    {req.status !== "Rejected" ? (
                      <>
                        {/* Open / View button */}
                        {(user.role === "Principal" || user.role === "store_manager") ? (
                          <button
                            className="ims-btn ims-btn-view"
                            title="Open"
                            onClick={() => handleOpenRequisition(req.id)}
                          >
                            <i className="fas fa-folder-open"></i>
                          </button>
                        ) : user.id === req.user_id ? (
                          <button
                            className="ims-btn ims-btn-view"
                            title="View"
                            onClick={() => handleOpenRequisition(req.id)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        ) : null}

                        {/* Edit — only creator + Pending */}
                        {user.id === req.user_id && req.status === "Pending" && (
                          <button
                            className="ims-btn ims-btn-edit"
                            title="Edit"
                            onClick={() => navigate(`/dashboard/edit-requisition/${req.id}`)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                        )}

                        {/* Print PDF — Issued or Partially Issued */}
                        {(req.status === "Approved" || req.status === "Issued" || req.status === "Partially Issued") && (
                          <button
                            className="ims-btn ims-btn-pdf"
                            title="Print PDF"
                            onClick={() => generateRequisitionPDF(req.id)}
                          >
                            <i className="fas fa-print"></i>
                          </button>
                        )}

                        {/* Return — store_manager only */}
                        {user.role === "store_manager" &&
                          (req.status === "Issued" || req.status === "Partially Issued") && (
                            <button
                              className="ims-btn ims-btn-return"
                              title="Return"
                              onClick={() => navigate(`/dashboard/return-requisition/${req.id}`)}
                            >
                              <i className="fas fa-undo"></i>
                            </button>
                          )}
                      </>
                    ) : (
                      <span className="text-muted" style={{ fontSize: "12px" }}>—</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  No requisitions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <ReactPaginate
        previousLabel={"← Previous"}
        nextLabel={"Next →"}
        breakLabel={"..."}
        pageCount={totalPages}
        marginPagesDisplayed={2}
        pageRangeDisplayed={5}
        onPageChange={handlePageClick}
        containerClassName={"pagination justify-content-center mt-3"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        breakClassName={"page-item"}
        breakLinkClassName={"page-link"}
        activeClassName={"active"}
      />
    </div>
  );
};

export default RequisitionList;
